/**
 * Live Automations client — the same `AutomationsClient` interface the UI is built against, backed by
 * the real agent-trust endpoints (`/sub-agents`, `/sub-agents/:id/config`, `/agent-approvals`).
 * Components and hooks are unchanged; `useAutomationsClient` selects this over the fixture client.
 *
 * Coverage is deliberately partial — it mirrors Phase-1 backend reality:
 *   • live: roster (list/config/enable/autonomy/delete) + the sign-off queue (list/decide).
 *   • fixture-fallback: runs, simulate, templates, activity, chat write-back — these have no
 *     backend yet, so they delegate to `fixtureAutomationsClient` (which returns nothing for a live
 *     agent id, i.e. honestly empty, and static UI starters for templates).
 *
 * `decideApproval` only RECORDS the decision server-side — nothing sends (execution is a
 * separately-gated step, not in this PR).
 */
import * as api from "@/lib/api/endpoints/agent-trust";
import type {
  StandingAgent,
  AgentApproval,
  ApiRunDetail,
  ApiActivityItem,
  ApiNotification,
} from "@/lib/api/endpoints/agent-trust";
import {
  fixtureAutomationsClient,
  deriveAutomationName,
  type AutomationsClient,
} from "./automationsClient";
import { CONSEQUENTIAL_ACTION_TYPES } from "@/common/utils/action-vocabulary";
import type {
  Automation,
  AutomationAction,
  Autonomy,
  PresetKind,
  Reversibility,
  RunningRun,
  TriggerMode,
} from "./fixtures";
import type {
  ApprovalItem,
  ProvenanceFlag,
  AutomationRunDetail,
  ActivityItem,
  AutomationNotification,
  NotificationKind,
  NotificationOutcome,
  RunStatus,
  RunMilestone,
  MilestoneKind,
} from "./fixtures-deep";

// Per-agent identity colours, assigned deterministically so a given agent keeps its colour
// across reloads (the backend doesn't store one). Mirrors the fixtures palette.
const PALETTE = ["#b4763f", "#a8546a", "#4f8a6b", "#7a5ea8", "#7a7f85"];
// Automation-less manual / ad-hoc durable runs (ADR-025) have a null agentId — render
// them with a stable label + neutral colour rather than a looked-up identity (#4065).
const MANUAL_RUN_LABEL = "Manual run";
const MANUAL_RUN_COLOR = "#7a7f85";
function colorFor(id: string | null | undefined): string {
  if (!id) return MANUAL_RUN_COLOR;
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

// Consequential action types (sign-off) render a red sign-off tag; everything else renders a
// "free" Build/Overview tag. This is display-only — the real fail-closed classification lives
// server-side. The set is now derived from the shared `CONSEQUENTIAL_ACTION_TYPES` mirror
// (common/utils/action-vocabulary) so it can't drift out of lockstep with the API ACTION_REGISTRY
// the way the old inline `SIGN_OFF_ACTIONS` did — that hand-maintained list was missing the ~13
// newly-consequential cross-object types (note/task/event/contact CRUD), so they wrongly badged
// as "free".
function reversibilityOf(actionType: string): Reversibility {
  return CONSEQUENTIAL_ACTION_TYPES.has(actionType) ? "sign-off" : "free";
}

function titleizeAction(actionType: string): string {
  // "email.send" → "Email send"; falls back to the raw type.
  const words = actionType.replace(/[._]/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : actionType;
}

function triggerModeOf(cfg?: Record<string, unknown>): TriggerMode {
  const kind = String(
    (cfg?.type ?? cfg?.kind ?? cfg?.mode ?? "") as string,
  ).toLowerCase();
  if (kind.includes("cron") || kind.includes("schedul")) return "scheduled";
  if (kind.includes("event") || kind.includes("react")) return "reactive";
  if (cfg?.cron || cfg?.schedule) return "scheduled";
  return "one-shot";
}

function triggerLabel(cfg?: Record<string, unknown>): string {
  if (!cfg) return "runs on demand";
  const cron = cfg.cron ?? cfg.schedule;
  if (typeof cron === "string" && cron) return cron;
  const mode = triggerModeOf(cfg);
  if (mode === "reactive") return "when something relevant happens";
  if (mode === "scheduled") return "on a schedule";
  return "runs on demand";
}

function relativeLabel(iso?: string): string | undefined {
  if (!iso) return undefined;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return undefined;
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(t).toLocaleDateString();
}

const PRESET_KINDS = new Set<string>([
  "inbox_watcher",
  "daily_briefing",
  "overnight_processing",
  "meeting_prep",
]);

function extractKind(cfg?: Record<string, unknown>): PresetKind | undefined {
  const k = cfg?.kind;
  if (typeof k === "string" && PRESET_KINDS.has(k)) return k as PresetKind;
  return undefined;
}

export function mapAutomation(
  s: StandingAgent,
  pendingByAgent: Map<string, number>,
): Automation {
  const autonomy = (s.autonomyLevel ?? "confirm") as Autonomy;
  const actions: AutomationAction[] = (s.actionAllowlist ?? []).map((t, i) => ({
    id: `${s.id}-act-${i}`,
    label: titleizeAction(t),
    reversibility: reversibilityOf(t),
  }));
  const lastRun = relativeLabel(s.lastRunAt);
  const kind = extractKind(s.triggerConfig);
  return {
    id: s.id,
    name: s.name,
    role: s.role ?? "Standing automation",
    color: colorFor(s.id),
    status: s.enabled === false ? "paused" : "active",
    autonomy,
    triggerMode: triggerModeOf(s.triggerConfig),
    pendingCount: pendingByAgent.get(s.id) ?? 0,
    lastRunLabel: lastRun,
    recentActivity: s.lastReportSummary ?? undefined,
    kind,
    preferences: s.preferences,
    // The raw allowlist drives the Customize panel's capability toggles (opt-in, default off).
    actionAllowlist: s.actionAllowlist ?? [],
    build: {
      trigger: triggerLabel(s.triggerConfig),
      condition: undefined,
      actions,
      guardrails: { neverNewDomains: true, flagUntrusted: true },
    },
    // Live runs aren't wired yet — Overview's "Its work" + the Runs lens render empty.
    runs: [],
  };
}

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

/**
 * True for an email-send action in EITHER vocabulary: the api spelling `email.send`
 * AND the x-agent tool spelling `send_email`. The sign-off mappers previously matched
 * only `startsWith("email")`, so live `send_email` approvals missed the To/Subject/Body
 * preview and fell back to a raw-JSON payload dump (go-live bug 4).
 */
function isEmailAction(actionType: string): boolean {
  return actionType.startsWith("email") || actionType === "send_email";
}

export function mapApproval(
  a: AgentApproval,
  roster: Map<string, { name: string; color: string }>,
): ApprovalItem {
  const meta = a.agentId ? roster.get(a.agentId) : undefined;
  const payload = a.preparedPayload ?? {};
  const prov = a.provenance ?? {};
  const isEmail = isEmailAction(a.actionType);
  const email = isEmail
    ? {
        to: str(payload.to) ?? str(payload.recipient) ?? "—",
        subject: str(payload.subject) ?? "(no subject)",
        body: str(payload.body) ?? str(payload.text) ?? "",
        sendTime: str(payload.sendTime),
      }
    : undefined;
  const read = Array.isArray(prov.read)
    ? (prov.read as unknown[]).map(String)
    : [];
  const flags: ProvenanceFlag[] = (a.noveltyFlags ?? []).map((f) => ({
    // The card surfaces flags whose param is "recipient" (the amber injection marker), so map
    // every recipient/injection signal there; other signals keep their raw token.
    param: isRecipientWarn(f) ? "recipient" : f,
    note: humanizeFlag(f),
  }));
  return {
    id: a.id,
    agentId: a.agentId,
    agentName: meta?.name ?? a.agentId ?? MANUAL_RUN_LABEL,
    agentColor: meta?.color ?? colorFor(a.agentId),
    actionType: a.actionType,
    email,
    why: {
      trigger: str(prov.trigger) ?? titleizeAction(a.actionType),
      read,
    },
    provenanceFlags: flags,
  };
}

// Recipient/injection signals that should light the card's amber marker. The real backend
// novelty flags are new_external_recipient | large_send | untrusted_input (action-gate.ts),
// plus body-sourced recipients on the prompt-injection surface.
function isRecipientWarn(flag: string): boolean {
  return (
    flag.includes("recipient") ||
    flag.includes("body") ||
    flag.includes("untrusted") ||
    flag.includes("external")
  );
}

function humanizeFlag(flag: string): string {
  if (flag.includes("body") || flag.includes("untrusted"))
    return "from the email, not your Contacts";
  if (flag === "new_external_recipient" || flag.includes("external"))
    return "new recipient — not in your Contacts";
  if (flag.includes("large")) return "an unusually large send";
  if (flag.includes("domain")) return "new domain — never contacted before";
  return flag.replace(/[._]/g, " ");
}

// ── Run / activity / notification mapping (live run-status feed) ──────────────

/**
 * Map a durable run status (+ pendingApproval) onto the web RunStatus vocabulary.
 *   pending approval / suspended → "pending" (awaiting the human)
 *   completed under autonomy     → "auto"   (else "approved")
 *   failed/cancelled             → "rejected"
 *   queued/running               → "pending"
 */
function mapRunStatus(run: ApiRunDetail): RunStatus {
  if (run.pendingApproval) return "pending";
  switch (run.status) {
    case "completed":
      return run.trigger === "scheduled" || run.trigger === "reactive"
        ? "auto"
        : "approved";
    case "failed":
    case "cancelled":
      return "rejected";
    case "queued":
    case "running":
    default:
      return "pending";
  }
}

// Run-event types → the four timeline milestone kinds (best-effort; the API ships
// the durable event log, the web renders it as a legible timeline).
function milestoneKindFor(eventType: string): MilestoneKind {
  const t = eventType.toLowerCase();
  if (t.includes("trigger") || t.includes("start") || t.includes("created"))
    return "trigger";
  if (t.includes("read") || t.includes("context") || t.includes("fetch"))
    return "read";
  if (t.includes("decide") || t.includes("decision") || t.includes("plan"))
    return "decided";
  if (t.includes("suspend") || t.includes("waiting") || t.includes("approval"))
    return "waiting";
  return "did";
}

function shortTime(iso?: string | null): string | undefined {
  if (!iso) return undefined;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return undefined;
  return new Date(t).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function mapRunDetail(run: ApiRunDetail): AutomationRunDetail {
  const milestones: RunMilestone[] = (run.events ?? []).map((e, i) => {
    const data = (e.data ?? {}) as Record<string, unknown>;
    const label =
      str(data.label) ?? str(data.message) ?? str(data.summary) ?? e.eventType;
    return {
      id: `${run.id}-m${e.seq ?? i}`,
      kind: milestoneKindFor(e.eventType),
      label,
      detail: str(data.detail) ?? str(data.summary),
      when: shortTime(e.createdAt),
    };
  });
  const detail: AutomationRunDetail = {
    id: run.id,
    agentId: run.agentId ?? "",
    when: shortTime(run.startedAt ?? run.createdAt) ?? "",
    status: mapRunStatus(run),
    trigger: run.taskDescription ?? run.trigger ?? "",
    read: [],
    decided: run.decision ?? run.resultSummary ?? "",
    did: [],
    milestones: milestones.length > 0 ? milestones : undefined,
  };
  // A pending approval surfaces the inline sign-off card on the run timeline.
  if (run.pendingApproval) {
    const p = run.pendingApproval;
    const payload = (p.preparedPayload ?? {}) as Record<string, unknown>;
    const isEmail = isEmailAction(p.actionType);
    detail.awaitingSignOff = {
      approvalId: p.id,
      actionType: p.actionType,
      email: isEmail
        ? {
            to: str(payload.to) ?? str(payload.recipient) ?? "—",
            subject: str(payload.subject) ?? "(no subject)",
            body: str(payload.body) ?? str(payload.text) ?? "",
            sendTime: str(payload.sendTime),
          }
        : undefined,
      args: isEmail ? undefined : payload,
      provenanceFlags: (p.noveltyFlags ?? []).map((f) => ({
        param: isRecipientWarn(f) ? "recipient" : f,
        note: humanizeFlag(f),
      })),
    };
  }
  return detail;
}

export function mapActivity(a: ApiActivityItem): ActivityItem {
  return {
    id: a.id,
    when: a.when,
    ts: a.ts ?? undefined,
    agentId: a.agentId,
    agentName: a.agentName,
    agentColor: a.agentColor || colorFor(a.agentId),
    summary: a.summary,
    // The prose the run produced, when the API linked one (#4959). Rendered as an
    // expandable secondary block in the Activity feed.
    body: a.body ?? undefined,
    status: (a.status as RunStatus) ?? "approved",
    trusted: a.trusted,
  };
}

function mapNotification(n: ApiNotification): AutomationNotification {
  return {
    id: n.id,
    kind: n.kind as NotificationKind,
    agentId: n.agentId,
    agentName: n.agentName,
    agentColor: n.agentColor || colorFor(n.agentId),
    runId: n.runId,
    summary: n.summary,
    when: n.when,
    outcome: n.outcome as NotificationOutcome | undefined,
    approvalId: n.approvalId,
  };
}

// An active run → a "Running now" strip row. The sub-line is the run's latest beat;
// the state pill is a terse status word. agentId routes the click to the agent card.
function mapRunningRow(run: ApiRunDetail): RunningRun {
  const lastEvent = run.events?.[run.events.length - 1];
  const evtData = (lastEvent?.data ?? {}) as Record<string, unknown>;
  const sub =
    str(evtData.label) ??
    str(evtData.message) ??
    str(run.taskDescription) ??
    "working…";
  const state = run.pendingApproval
    ? "awaiting sign-off"
    : run.pauseState
      ? "waiting"
      : run.status === "queued"
        ? "queued"
        : "working";
  return {
    id: run.id,
    title: run.agentName ?? str(run.taskDescription) ?? "Automation run",
    sub,
    state,
    agentId: run.agentId ?? undefined,
  };
}

export function createLiveAutomationsClient(
  token: string,
  workspaceId: string,
): AutomationsClient {
  async function rosterMaps() {
    const [{ items: agents }, { items: approvals }] = await Promise.all([
      api.listStandingAgents(token, workspaceId),
      api.listApprovals(token, workspaceId),
    ]);
    const pendingByAgent = new Map<string, number>();
    for (const ap of approvals) {
      // Automation-less approvals (null agentId) have no roster card to count against —
      // they surface in the sign-off inbox, not as a per-agent badge (#4065).
      if (ap.status === "pending" && ap.agentId)
        pendingByAgent.set(
          ap.agentId,
          (pendingByAgent.get(ap.agentId) ?? 0) + 1,
        );
    }
    return { agents, approvals, pendingByAgent };
  }

  return {
    async listAutomations() {
      const { agents, pendingByAgent } = await rosterMaps();
      return agents.map((a) => mapAutomation(a, pendingByAgent));
    },
    async getAutomation(id) {
      const { agents, pendingByAgent } = await rosterMaps();
      const found = agents.find((a) => a.id === id);
      return found ? mapAutomation(found, pendingByAgent) : null;
    },
    async setEnabled(id, enabled) {
      const updated = await api.updateAgentConfig(token, workspaceId, id, {
        enabled,
      });
      // pendingCount on this returned agent is 0 (no approvals join here); callers use
      // .mutate() and re-read the roster via onSuccess invalidation, so counts stay correct.
      return mapAutomation(updated, new Map());
    },
    async setAutonomy(id, autonomy) {
      const updated = await api.updateAgentConfig(token, workspaceId, id, {
        autonomyLevel: autonomy,
      });
      return mapAutomation(updated, new Map());
    },
    async setPreferences(id, preferences) {
      // The body IS the raw preferences object; the server validates + stores it.
      await api.setPreferences(token, workspaceId, id, preferences);
    },
    async setActionAllowlist(id, actionAllowlist) {
      // Capability opt-in — the config PATCH validates the list against the server
      // ACTION_VOCABULARY (D7), so an unknown type is rejected 400 rather than stored.
      const updated = await api.updateAgentConfig(token, workspaceId, id, {
        actionAllowlist,
      });
      return mapAutomation(updated, new Map());
    },
    async deleteAutomation(id) {
      await api.deleteStandingAgent(token, workspaceId, id);
    },
    async listApprovals() {
      const { agents, approvals } = await rosterMaps();
      const roster = new Map(
        agents.map((a) => [a.id, { name: a.name, color: colorFor(a.id) }]),
      );
      return approvals
        .filter((a) => a.status === "pending")
        .map((a) => mapApproval(a, roster));
    },
    async decideApproval(id, decision, preparedPayload) {
      // Records only — never sends. Execution-on-approve is separately gated.
      // Forward preparedPayload so an "edited" decision persists the user's edits.
      await api.decideApproval(
        token,
        workspaceId,
        id,
        decision,
        preparedPayload,
      );
    },

    // ── Live run-status feed (§3/§4) ──
    async listRuns(agentId) {
      const { items } = await api.listAgentRuns(token, workspaceId, agentId);
      return items.map(mapRunDetail);
    },
    async listActivity() {
      const { items } = await api.listAgentActivity(token, workspaceId);
      return items.map(mapActivity);
    },
    async listNotifications() {
      const { items } = await api.listAgentNotifications(token, workspaceId);
      return items.map(mapNotification);
    },
    async listRunningNow() {
      const { items } = await api.listActiveRuns(token, workspaceId);
      return items.map(mapRunningRow);
    },

    // ── Not yet backed by an endpoint → fixture fallback (honestly empty for live ids) ──
    listTemplates: fixtureAutomationsClient.listTemplates,
    simulate: fixtureAutomationsClient.simulate,
    applyChatRefinement: fixtureAutomationsClient.applyChatRefinement,
    undoRefinement: fixtureAutomationsClient.undoRefinement,
    async createAutomation(purpose) {
      const created = await api.createStandingAgent(token, workspaceId, {
        name: deriveAutomationName(purpose),
        focusArea: purpose,
      });
      // The create route can't set standing fields, so follow with a config PATCH that starts
      // the agent safe (paused, suggest-only) until it's finished in Build — mirrors the
      // fixture scaffold's defaults.
      const configured = await api.updateAgentConfig(
        token,
        workspaceId,
        created.id,
        { enabled: false, autonomyLevel: "suggest", role: purpose },
      );
      return mapAutomation(configured, new Map());
    },
    // Color is client-derived (colorFor), not stored server-side — setColor is an intentional
    // no-op on the live path (it reverts on the next roster refetch).
    setColor: fixtureAutomationsClient.setColor,
  };
}
